import { api } from './api';

class ImmigrationNewsService {
  // Get all immigration news with filters
  async getNews(params = {}) {
    try {
      const response = await api.get('/immigration-news/', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching immigration news:', error);
      throw error;
    }
  }

  // Get featured news
  async getFeaturedNews() {
    try {
      const response = await api.get('/immigration-news/featured/');
      return response.data.results || [];
    } catch (error) {
      console.error('Error fetching featured news:', error);
      throw error;
    }
  }

  // Get recent news (last 7 days)
  async getRecentNews() {
    try {
      const response = await api.get('/immigration-news/recent/');
      return response.data.results || [];
    } catch (error) {
      console.error('Error fetching recent news:', error);
      throw error;
    }
  }

  // Get news by country
  async getNewsByCountry(country) {
    try {
      const response = await api.get(`/immigration-news/country/${country}/`);
      return response.data.results || [];
    } catch (error) {
      console.error('Error fetching news by country:', error);
      throw error;
    }
  }

  // Get news by category
  async getNewsByCategory(category) {
    try {
      const response = await api.get(`/immigration-news/category/${category}/`);
      return response.data.results || [];
    } catch (error) {
      console.error('Error fetching news by category:', error);
      throw error;
    }
  }

  // Get news detail
  async getNewsDetail(id) {
    try {
      const response = await api.get(`/immigration-news/${id}/`);
      return response.data;
    } catch (error) {
      console.error('Error fetching news detail:', error);
      throw error;
    }
  }

  // Get related news
  async getRelatedNews(newsId) {
    try {
      const response = await api.get(`/immigration-news/${newsId}/related/`);
      return response.data.related_news || [];
    } catch (error) {
      console.error('Error fetching related news:', error);
      throw error;
    }
  }

  // Social interactions
  async toggleLike(newsId) {
    try {
      const response = await api.post(`/immigration-news/${newsId}/like/`);
      return response.data;
    } catch (error) {
      console.error('Error toggling like:', error);
      throw error;
    }
  }

  async getComments(newsId) {
    try {
      const response = await api.get(`/immigration-news/${newsId}/comments/`);
      return response.data;
    } catch (error) {
      console.error('Error fetching comments:', error);
      throw error;
    }
  }

  async addComment(newsId, content) {
    try {
      const response = await api.post(`/immigration-news/${newsId}/comments/`, {
        content: content
      });
      return response.data;
    } catch (error) {
      console.error('Error adding comment:', error);
      throw error;
    }
  }

  async deleteComment(newsId, commentId) {
    try {
      const response = await api.delete(`/immigration-news/${newsId}/comments/${commentId}/delete/`);
      return response.data;
    } catch (error) {
      console.error('Error deleting comment:', error);
      throw error;
    }
  }

  async shareNews(newsId, platform = 'direct') {
    try {
      const response = await api.post(`/immigration-news/${newsId}/share/`, {
        platform: platform
      });
      return response.data;
    } catch (error) {
      console.error('Error sharing news:', error);
      throw error;
    }
  }

  // Search news
  async searchNews(query, filters = {}) {
    try {
      const params = { q: query, ...filters };
      const response = await api.get('/immigration-news/search/', { params });
      return response.data;
    } catch (error) {
      console.error('Error searching news:', error);
      throw error;
    }
  }

  // Get news statistics
  async getNewsStatistics() {
    try {
      const response = await api.get('/immigration-news/statistics/');
      return response.data;
    } catch (error) {
      console.error('Error fetching news statistics:', error);
      throw error;
    }
  }

  // Create news alert
  async createNewsAlert(alertData) {
    try {
      const response = await api.post('/immigration-news/alerts/create/', alertData);
      return response.data;
    } catch (error) {
      console.error('Error creating news alert:', error);
      throw error;
    }
  }

  // Get user alerts
  async getUserAlerts() {
    try {
      const response = await api.get('/immigration-news/alerts/');
      return response.data;
    } catch (error) {
      console.error('Error fetching user alerts:', error);
      throw error;
    }
  }

  // Delete news alert
  async deleteNewsAlert(alertId) {
    try {
      const response = await api.delete(`/immigration-news/alerts/${alertId}/delete/`);
      return response.data;
    } catch (error) {
      console.error('Error deleting news alert:', error);
      throw error;
    }
  }

  // Trigger news fetch (admin only)
  async triggerNewsFetch() {
    try {
      const response = await api.post('/immigration-news/admin/fetch/');
      return response.data;
    } catch (error) {
      console.error('Error triggering news fetch:', error);
      throw error;
    }
  }

  // Helper method to format news data for display
  formatNewsForDisplay(news) {
    return {
      ...news,
      formattedDate: this.formatDate(news.published_date),
      timeAgo: news.time_ago,
      priorityColor: this.getPriorityColor(news.priority),
      countryFlag: this.getCountryFlag(news.country),
      categoryIcon: this.getCategoryIcon(news.category)
    };
  }

  // Format date for display
  formatDate(dateString) {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  // Get priority color
  getPriorityColor(priority) {
    const colors = {
      'urgent': 'text-red-600 bg-red-100',
      'high': 'text-orange-600 bg-orange-100',
      'medium': 'text-blue-600 bg-blue-100',
      'low': 'text-gray-600 bg-gray-100'
    };
    return colors[priority] || colors.medium;
  }

  // Get country flag emoji
  getCountryFlag(country) {
    const flags = {
      'USA': '🇺🇸',
      'UK': '🇬🇧',
      'Canada': '🇨🇦',
      'Australia': '🇦🇺',
      'Germany': '🇩🇪',
      'France': '🇫🇷',
      'Pakistan': '🇵🇰',
      'India': '🇮🇳',
      'China': '🇨🇳',
      'Japan': '🇯🇵'
    };
    return flags[country] || '🌍';
  }

  // Get category icon
  getCategoryIcon(category) {
    const icons = {
      'visa': '📋',
      'policy': '📜',
      'refugee': '🏠',
      'border': '🛂',
      'employment': '💼',
      'general': '📰'
    };
    return icons[category] || icons.general;
  }

  // Get sentiment color
  getSentimentColor(sentiment) {
    const colors = {
      'positive': 'text-green-600 bg-green-100',
      'negative': 'text-red-600 bg-red-100',
      'neutral': 'text-gray-600 bg-gray-100'
    };
    return colors[sentiment] || colors.neutral;
  }
}

const immigrationNewsService = new ImmigrationNewsService();
export default immigrationNewsService;
